// ** React Imports
import { Fragment, useEffect, useState } from 'react';

// ** MUI Imports
import {
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip
} from "@mui/material";
import { styled } from '@mui/material/styles';
import MuiCard from '@mui/material/Card';

// ** Icon Imports
import Icon from "./icon";

// ** Third-Party Imports
import Papa from 'papaparse'
import * as XLSX from 'xlsx'

import xlsxImage from '../images/xlsx.png';
import csvImage from '../images/csv.png';
// import ExcelCSVImport from "../../../components/ExcelCSVImport";

import CustomerBulkImportDialog from 'screens/Customers/CustomerBulkImport';
import JobsiteBulkImport from 'screens/Customers/JobsiteBulkImport';
import ContactBulkImport from 'screens/Customers/ContactBulkImport';
// import EmployeeBulkImport from 'screens/Employees/EmployeeBulkImport';

const renderFilePreview = (file) => {
  let icon = file.name.includes('.xlsx') ? xlsxImage :  csvImage; 
  return <img src={icon} height="80"/>;
};

const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '100%' }
}));

const BulkImport = ({
  open,
  handleClose,
  content,
  files,
  loading,
  getRootProps,
  getInputProps,
  setFiles,
  navigate,
  setLoading,  
  fetchContactData = () => {},
  fetchJobsiteData = () => {}
}) => {
  // ** States
  const [message, setMessage] = useState('');
  const [openMappingDlg, setOpenMappingDlg] = useState(false);
  const [openJobsiteDlg, setOpenJobsiteDlg] = useState(false);
  const [data, setData] = useState([]);
  // const [loading, setLoading] = useState(false);
  
  const handleOpenMappingDlg = () => {
    setOpenMappingDlg(true);
  }
  
  const handleCloseMappingDlg = () => {
    setOpenMappingDlg(false);
  }

  const handleCloseJobsiteDlg = () => {
    setOpenJobsiteDlg(false);
  }

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleFileParse = async () => {
    
    const file = files[0]
    if (!file) {
      setMessage('Please select a file to import.');
      return
    }
  
    let parsedData = []
  
    const renameKeys = (data) => {
      return data.map(row => {
        const newRow = {}
        const keyGroups = {}
  
        for (let key in row) {
          const match = key.match(/(.+)_([0-9]+)/)
          if (match) {
            const baseKey = match[1]
            const number = parseInt(match[2])

            if (!keyGroups[baseKey]) {
              keyGroups[baseKey] = []
            }
            keyGroups[baseKey].push(number)
          } else {
            keyGroups[key] = [0]  
          }
        }

        for (let key in row) {
          const match = key.match(/(.+)_([0-9]+)/)
          if (match) {
            const baseKey = match[1]
            const number = parseInt(match[2])

            const numbers = keyGroups[baseKey].sort((a, b) => a - b)
            const isValidSequence = numbers.every((num, index) => num === index)
  
            if (isValidSequence) {
          
              newRow[`${baseKey}_${number + 1}`] = row[key]
            } else {
              newRow[key] = row[key]
            }
          } else {
            newRow[key] = row[key]
          }
        }
        return newRow
      })
    }
  
    if (file.type === 'text/csv') {
      Papa.parse(file, {
        skipEmptyLines: true,
        header: true,
        complete: (result) => {
          parsedData = renameKeys(result.data)
          navigateToNextPage(parsedData)
          setLoading(false)
        }
      })
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
    
        // Parse with header row and default value for empty cells
        let dataRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        });
    
        // Extract headers and map rows
        const headers = dataRows[0];
        const rows = dataRows.slice(1).map(row =>
          headers.reduce((acc, key, i) => {
            acc[key] = row[i] || '';
            return acc;
          }, {})
        );
    
        parsedData = renameKeys(rows);
        navigateToNextPage(parsedData);
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    }
  }  

  const navigateToNextPage = (data) => {    
    setData(data); 
    let customerID = content?.customerID;     
    if(content.ImportURL==="/customers/add-bulk-contacts"){
      setOpenMappingDlg(true);
    } else if (content.ImportURL==="/customers/add-bulk-jobsites") {
      setOpenJobsiteDlg(true);
    } 
    // else if (content.ImportURL==="/onboarding/add-customer-with-jobsites") {
    //   // setOpenJobsiteDlg(true);
    // } else if (content.ImportURL==="/onboarding/add-customer-with-contacts") {
    //   // setOpenJobsiteDlg(true);
    // }
    else {
      navigate(content.ImportURL, { state: { data , customerID, fromDetail: true } }) 
    }      
  }

  const fileList = files.map(file => (
      <ListItem key={file.name} sx={{p:0}}>
        <Card sx={{ padding: '10px !important' }}>
          <CardContent sx={{ padding: '0px !important' }}>
            <Box sx={{display: 'flex', gap: 2}}>
              <Box>{renderFilePreview(file)}</Box>
              <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                  <Box>
                    <Typography className='file-name'>{file.name}</Typography>
                    <Typography className='file-size' variant='body2'>
                      {Math.round(file.size / 100) / 10 > 1000
                      ? `${(Math.round(file.size / 100) / 10000).toFixed(1)} mb`
                      : `${(Math.round(file.size / 100) / 10).toFixed(1)} kb`}
                    </Typography> 
                  </Box>
                  <Tooltip title='Remove File'>
                    <IconButton onClick={() => handleRemoveFile(file)}>
                      <Icon icon='tabler:x' fontSize={20} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 4}}>
                  <Box sx={{width: '100%'}}>
                    <LinearProgress value={100} variant="determinate"
                      sx={{
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'success.main'
                        },
                        height: '10px'
                      }}
                    />
                  </Box>
                  <Box color="success.main">
                    <Icon icon="tabler:circle-check-filled" />
                  </Box>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </ListItem>
));

  const handleRemoveFile = (file) => {
      const filteredFiles = files?.filter(i => i.name !== file.name);
      setFiles(filteredFiles);
  };
  
  return (
    <>
      <Dialog
          fullWidth={true}
          maxWidth="sm"
          open={open}
          onClose={handleClose}
          aria-labelledby="form-dialog-title"
          PaperProps={{
              sx: {
                  maxHeight: '90vh',
                  minWidth: '40vw',
                  overflow:'hidden',
              },
          }}
      >
        <DialogTitle sx={{borderBottom: '1px solid #D9D9D9', paddingBottom: '20px !important'}}>
          <Box
            sx={{
              display: 'flex',
              alignItem: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h3" fontWeight='bold'>Import bulk {content.title}</Typography>
            <IconButton onClick={handleClose}>
              <Icon icon='tabler:x' />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
            <Grid container spacing={5} my={4}>
                <Grid item xs={12}>
                    {message && (
                      <Box mb={2} textAlign="center">
                        <Typography variant="h6" color={message.includes('successfully') ? 'green' : 'red'}>
                          {message.split('\n').map((line, index) => (
                            <div key={index}>{line}</div>
                          ))}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ textAlign: 'center' }}>                                
                        <Typography variant='h5'>Import your {content.title} to Cleri</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Card sx={{ cursor: 'pointer' }}>
                        <CardContent>
                                <div {...getRootProps({ className: 'dropzone' })}>
                                    <input {...getInputProps()} />
                                    <Box sx={{ display: 'flex', textAlign: 'center', alignItems: 'center', flexDirection: 'column' }}>
                                        <Box
                                            sx={{
                                                mb: 6,
                                                width: 48,
                                                height: 48,
                                                display: 'flex',
                                                borderRadius: 1,
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: theme => `rgba(${theme.palette.customColors.main}, 0.08)`
                                            }}
                                        >
                                            <Icon icon='tabler:upload' fontSize='1.75rem' />
                                        </Box>
                                        <Typography variant='h4' sx={{ mb: 2 , fontSize:'1.2rem'}}>
                                            Click to Browse or Drag & Drop files here
                                        </Typography>
                                        <List>
                                          <ListItem disablePadding>
                                            <ListItemIcon>
                                              <Icon icon='tabler:circle-filled' height={7} width={7} />
                                            </ListItemIcon>
                                            <ListItemText>
                                              Accepted formats: CSV and XLSX
                                            </ListItemText>
                                          </ListItem>
                                          <ListItem disablePadding>
                                            <ListItemIcon>
                                              <Icon icon='tabler:circle-filled' height={7} width={7} />
                                            </ListItemIcon>
                                            <ListItemText>
                                              Size limit: File size must be 2MB or under
                                            </ListItemText>
                                          </ListItem>
                                          <ListItem disablePadding>
                                            <ListItemIcon>
                                              <Icon icon='tabler:circle-filled' height={7} width={7} />
                                            </ListItemIcon>
                                            <ListItemText>
                                              Only one (1) file can be uploaded at once
                                            </ListItemText>
                                          </ListItem>
                                        </List>
                                    </Box>
                                </div>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                  {files.length ? (
                    <Fragment>
                        <List>{fileList}</List>
                    </Fragment>
                  ) : null}
                </Grid>                  
            </Grid>
        </DialogContent>
        <DialogActions
          sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                padding: '20px 24px !important', 
                boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`, 
                zIndex: 1 
            }}
          className="dialog-actions-dense"
        >
          <Box>
            <Button
              variant="contained"
              color="info"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            <Button
                variant='contained'
                color='main'
                disabled={loading}
                onClick={handleFileParse}
            >
                {loading ? <CircularProgress size={24} /> : 'Import'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      
      {/* Mapping Dialog */}
      {openMappingDlg && (
        <ContactBulkImport 
          open={openMappingDlg}
          handleClose={handleCloseMappingDlg}
          data={data}
          handleCloseImportDlg={handleClose}
          CustomerID={content?.customerID}
          msg={message}
          fetchContactData={fetchContactData}
        />
      )}

      {openJobsiteDlg && (
        <JobsiteBulkImport 
          open={openJobsiteDlg}
          handleClose={handleCloseJobsiteDlg}
          data={data}
          handleCloseImportDlg={handleClose}
          CustomerID={content?.customerID}
          msg={message}
          fetchJobsiteData={fetchJobsiteData}
        />
      )}
      {/* <CustomerBulkImportDialog 
        open={openMappingDlg}
        handleClose={handleCloseMappingDlg}
        data={data}
        CustomerID={content?.customerID}
        msg={message}
      /> */}
    </>
  );
};

export default BulkImport;
